import {
  ChangeEventHandler,
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { connect, getDataClass } from 'scrivito'

import {
  DEFAULT_EXPORT_HEADER_NAME_MODE,
  EXPORT_MODE_STEP,
  EXPORT_STEP,
  EXPORT_STEP_COUNT,
  PINNED_ATTRIBUTE_KEY,
  REVIEW_PREVIEW_TRANSITION_DURATION,
  REVIEW_PREVIEW_TRANSITION_MS,
  REVIEW_STEP,
  SELECT_COLUMNS_STEP,
} from './const'
import {
  CSV_EXPORT_PREVIEW_ROWS,
  type ExportHeaderNameMode,
  type ExportProgress,
  exportListPersonsToCsv,
  getAttributeValueByKey,
  getDefaultExportAttributeKeys,
  getHeaderLabel,
  getRecommendedExportAttributeKeys,
  MAX_SYNC_CSV_EXPORT_ROWS,
} from './exportListPersonsToCsv'
import { useReviewPreviewSidecar } from './hooks/useReviewPreviewSidecar'
import { useSelectedItemLayoutAnimation } from './hooks/useSelectedItemLayoutAnimation'
import { SortableSelectedAttributeItem } from './SortableSelectedAttributeItem'
import {
  normalizeStoredExportAttributeKeys,
  readStoredExportConfig,
  writeStoredExportConfig,
} from './storage'
import {
  type ExportAttributeDefinition,
  type ExportCsvModalProps,
  type ExportMode,
  type ExportModeSelection,
  type ExportProgressStage,
  type PreviewRow,
  type QuickExportVariant,
} from './types'
import {
  areArraysEqual,
  buildAttributeDefinitions,
  buildFilteredGroups,
  insertKeyInReferenceOrder,
  isDefined,
  reorderKeysByReference,
  stringifyPreviewValue,
} from './utils'
import { Icon } from '@/Atomic/Atoms/Icon'
import { ControlledCheckbox } from '@/Atomic/Atoms/Inputs/ControlledCheckbox'
import { Radio } from '@/Atomic/Atoms/Radio'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { StepTimeline } from '@/Atomic/Molecules/StepTimeline'
import {
  WizardModalFooter,
  WizardModalFooterNav,
  WizardModalLayout,
  WizardModalProgressBar,
  WizardModalProgressState,
  WizardModalStepContent,
} from '@/Atomic/Molecules/WizardModal'
import { Modal } from '@/Atomic/Organisms/Modal'
import { KeyboardSensor, PointerSensor } from '@/Components/KanbanView/Sensors'
import { errorToast } from '@/Components/Toast'
import { CONTACT_DESC_ORDER } from '@/Data/const'
import { ContactListRelation } from '@/Data/ContactListRelation/ContactListRelationDataClass'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { isString } from '@/utils/isString'
import { reportError } from '@/utils/reportError'

import './ExportCsvModal.scss'

export const ExportCsvModal: React.FC<ExportCsvModalProps> = connect(
  ({ isOpen, listId, contactEntity, listTitle, onClose }) => {
    const { t } = useTranslation()
    const schemaName = getSchemaName(contactEntity)
    const [step, setStep] = useState(EXPORT_MODE_STEP)
    const [visitedSteps, setVisitedSteps] = useState<number[]>([
      EXPORT_MODE_STEP,
    ])
    const [modeSelection, setModeSelection] =
      useState<ExportModeSelection | null>(null)
    const [searchValue, setSearchValue] = useState('')
    const [storedAttributeKeys, setStoredAttributeKeys] = useState<string[]>([])
    const [storedHeaderNameMode, setStoredHeaderNameMode] =
      useState<ExportHeaderNameMode>(DEFAULT_EXPORT_HEADER_NAME_MODE)
    const [exportHeaderNameMode, setExportHeaderNameMode] =
      useState<ExportHeaderNameMode>(DEFAULT_EXPORT_HEADER_NAME_MODE)
    const {
      closeReviewPreview,
      isReviewPreviewClosing,
      isReviewPreviewOpen,
      resetReviewPreview,
      toggleReviewPreview,
    } = useReviewPreviewSidecar(REVIEW_PREVIEW_TRANSITION_MS)
    const [invalidDraggedAttributeKey, setInvalidDraggedAttributeKey] =
      useState<string | null>(null)
    const previousStepRef = useRef(EXPORT_MODE_STEP)
    const [selectedAttributeKeys, setSelectedAttributeKeys] = useState<
      string[]
    >([])
    const [lastExportAttributeKeys, setLastExportAttributeKeys] = useState<
      string[]
    >([])
    const [lastExportHeaderNameMode, setLastExportHeaderNameMode] =
      useState<ExportHeaderNameMode>(DEFAULT_EXPORT_HEADER_NAME_MODE)
    const [isExporting, setIsExporting] = useState(false)
    const [isExportDone, setIsExportDone] = useState(false)
    const [hasExportError, setHasExportError] = useState(false)
    const selectedListRef = useRef<HTMLDivElement | null>(null)
    const { animateSelectedItemsFrom, captureSelectedItemRects } =
      useSelectedItemLayoutAnimation(selectedListRef)
    const [downloadCsvAgain, setDownloadCsvAgain] = useState<
      (() => void) | undefined
    >()
    const [exportProgress, setExportProgress] = useState<ExportProgress>({
      completed: 0,
      total: 0,
    })
    const [exportProgressStage, setExportProgressStage] =
      useState<ExportProgressStage>('preparing')
    const selectedColumnSensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    )

    const resetReviewState = useCallback(() => {
      resetReviewPreview()
      setInvalidDraggedAttributeKey(null)
    }, [resetReviewPreview])

    useEffect(() => {
      if (!isOpen) return

      const storedExportConfig = readStoredExportConfig(schemaName)

      setStep(EXPORT_MODE_STEP)
      previousStepRef.current = EXPORT_MODE_STEP
      setVisitedSteps([EXPORT_MODE_STEP])
      setModeSelection(null)
      setSearchValue('')
      setStoredAttributeKeys(storedExportConfig?.attributeKeys ?? [])
      setStoredHeaderNameMode(
        storedExportConfig?.headerNameMode ?? DEFAULT_EXPORT_HEADER_NAME_MODE,
      )
      setExportHeaderNameMode(DEFAULT_EXPORT_HEADER_NAME_MODE)
      resetReviewState()
      setSelectedAttributeKeys([])
      setLastExportAttributeKeys([])
      setLastExportHeaderNameMode(DEFAULT_EXPORT_HEADER_NAME_MODE)
      setIsExporting(false)
      setIsExportDone(false)
      setHasExportError(false)
      setDownloadCsvAgain(undefined)
      setExportProgress({ completed: 0, total: 0 })
      setExportProgressStage('preparing')
    }, [isOpen, listId, schemaName, resetReviewState])

    useEffect(() => {
      if (!isOpen) return

      setVisitedSteps((current) => {
        const nextVisitedSteps =
          step < previousStepRef.current
            ? current.filter((visitedStep) => visitedStep <= step)
            : current

        return nextVisitedSteps.includes(step)
          ? nextVisitedSteps
          : [...nextVisitedSteps, step]
      })
      previousStepRef.current = step
    }, [isOpen, step])

    const { groupsOrder, schemaGroups, schemaAttributes } =
      getSchemaAttributes(schemaName)
    const contactDataClass = getDataClass(contactEntity)

    const attributeDefinitions = buildAttributeDefinitions(
      groupsOrder,
      schemaGroups,
      schemaAttributes,
      t('components.csv.export.otherAttributes', 'Other attributes'),
    )
    const attributeDefinitionMap = new Map(
      attributeDefinitions.map((definition) => [definition.key, definition]),
    )

    const allSelectableKeys = getDefaultExportAttributeKeys(
      groupsOrder,
      schemaGroups,
      schemaAttributes,
    )
    const storedExportAttributeKeys = normalizeStoredExportAttributeKeys(
      storedAttributeKeys,
      allSelectableKeys,
    )
    const hasStoredExportConfiguration = storedExportAttributeKeys.length > 0
    const recommendedAttributeKeys = getRecommendedExportAttributeKeys(
      groupsOrder,
      schemaGroups,
      schemaAttributes,
    )
    const effectiveSelectedAttributeKeys = selectedAttributeKeys.length
      ? selectedAttributeKeys
      : allSelectableKeys

    const relationScope = ContactListRelation.all().transform({
      filters: {
        list: listId,
        contactEntity,
      },
      order: [CONTACT_DESC_ORDER],
    })
    const totalCount = isOpen ? (relationScope.count() ?? 0) : 0

    const previewContacts = isOpen
      ? ContactListRelation.all()
          .transform({
            filters: {
              list: listId,
              contactEntity,
            },
            order: [CONTACT_DESC_ORDER],
            limit: CSV_EXPORT_PREVIEW_ROWS,
          })
          .take()
          .map((relation) => relation.get('contact'))
          .filter(isString)
          .map((contactId) => contactDataClass?.get(contactId))
          .filter(isDefined)
      : []

    const selectedAttributeSet = new Set(effectiveSelectedAttributeKeys)
    const filteredAttributeGroups = buildFilteredGroups(
      attributeDefinitions,
      searchValue,
    )
    const selectedAttributes = effectiveSelectedAttributeKeys
      .map((key) => {
        if (key === PINNED_ATTRIBUTE_KEY) {
          return {
            key,
            label: getHeaderLabel(key, schemaAttributes, exportHeaderNameMode),
            groupKey: 'fixed',
            groupTitle: t(
              'components.csv.export.fixedColumns',
              'Fixed columns',
            ),
          }
        }

        const attributeDefinition = attributeDefinitionMap.get(key)
        if (!attributeDefinition) return undefined

        return {
          ...attributeDefinition,
          label: getHeaderLabel(key, schemaAttributes, exportHeaderNameMode),
        }
      })
      .filter(isDefined)

    const previewRows: PreviewRow[] = previewContacts.map((contact) => ({
      id: contact.id(),
      values: effectiveSelectedAttributeKeys.map((key) =>
        stringifyPreviewValue(
          getAttributeValueByKey(key, schemaAttributes, contact),
        ),
      ),
    }))

    const isTooLarge = totalCount > MAX_SYNC_CSV_EXPORT_ROWS
    const hasSelectedExportColumns = effectiveSelectedAttributeKeys.length > 0
    const canStartExport = !isExporting && !isTooLarge && totalCount > 0
    // Derive the legacy export mode / quick variant from the flat selection so
    // the downstream export logic keeps working with a single radio choice.
    const exportMode: ExportMode | null =
      modeSelection === null
        ? null
        : modeSelection === 'configure'
          ? 'configure'
          : 'quick'
    const quickExportVariant: QuickExportVariant | null =
      modeSelection === 'lastUsed'
        ? 'lastUsed'
        : modeSelection === 'allColumns'
          ? 'default'
          : null
    const quickExportAttributeKeys =
      quickExportVariant === 'lastUsed' && hasStoredExportConfiguration
        ? storedExportAttributeKeys
        : allSelectableKeys
    const quickExportHeaderNameMode =
      quickExportVariant === 'lastUsed' && hasStoredExportConfiguration
        ? storedHeaderNameMode
        : DEFAULT_EXPORT_HEADER_NAME_MODE
    const canContinueMode =
      modeSelection !== null &&
      (modeSelection === 'configure' ? !isTooLarge : canStartExport)
    const canContinueSelection = hasSelectedExportColumns
    const canStartConfiguredExport = canStartExport && hasSelectedExportColumns
    const hasSearchResults = filteredAttributeGroups.length > 0
    const isAllFieldsSelection = areArraysEqual(
      effectiveSelectedAttributeKeys,
      allSelectableKeys,
    )
    const isRecommendedSelection = areArraysEqual(
      effectiveSelectedAttributeKeys,
      recommendedAttributeKeys,
    )
    const exportProgressPercent =
      exportProgress.total > 0
        ? Math.min(
            100,
            Math.round((exportProgress.completed / exportProgress.total) * 100),
          )
        : 0
    const displayedExportProgressPercent = isExportDone
      ? 100
      : exportProgressPercent
    const isPreparingExport = exportProgressStage === 'preparing'
    const isExportStep = step === EXPORT_STEP
    const isReviewPreviewVisible =
      step === REVIEW_STEP && (isReviewPreviewOpen || isReviewPreviewClosing)
    const stepTitles = [
      t('components.csv.export.steps.mode', 'Choose export mode'),
      t('components.csv.export.steps.columns', 'Select columns'),
      t('components.csv.export.steps.review', 'Order & review'),
      t('components.csv.export.steps.export', 'Export'),
    ]

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearchValue(event.target.value)
    }

    const handleClose = () => {
      if (isExporting) return

      setStep(EXPORT_MODE_STEP)
      previousStepRef.current = EXPORT_MODE_STEP
      setVisitedSteps([EXPORT_MODE_STEP])
      setModeSelection(null)
      resetReviewState()
      setIsExportDone(false)
      setHasExportError(false)
      setDownloadCsvAgain(undefined)
      setExportProgress({ completed: 0, total: 0 })
      setExportProgressStage('preparing')
      onClose()
    }

    const handleTimelineStepClick = (targetStep: number) => {
      if (
        isExporting ||
        targetStep === step ||
        !visitedSteps.includes(targetStep)
      ) {
        return
      }

      if (targetStep !== REVIEW_STEP) {
        resetReviewState()
      }

      if (targetStep < step) {
        setVisitedSteps((current) =>
          current.filter((visitedStep) => visitedStep <= targetStep),
        )
      }

      setStep(targetStep)
    }

    const handleBack = () => {
      if (isExporting) return

      if (step === SELECT_COLUMNS_STEP) {
        resetReviewState()
        setVisitedSteps((current) =>
          current.filter((visitedStep) => visitedStep <= EXPORT_MODE_STEP),
        )
        setStep(EXPORT_MODE_STEP)
        return
      }

      if (step === REVIEW_STEP) {
        resetReviewState()
        setVisitedSteps((current) =>
          current.filter((visitedStep) => visitedStep <= SELECT_COLUMNS_STEP),
        )
        setStep(SELECT_COLUMNS_STEP)
        return
      }

      if (step === EXPORT_STEP && hasExportError) {
        resetReviewState()
        setVisitedSteps((current) =>
          current.filter(
            (visitedStep) =>
              visitedStep <=
              (exportMode === 'quick' ? EXPORT_MODE_STEP : REVIEW_STEP),
          ),
        )
        setStep(exportMode === 'quick' ? EXPORT_MODE_STEP : REVIEW_STEP)
      }
    }

    const toggleAttribute = (attributeKey: string) => {
      if (attributeKey === PINNED_ATTRIBUTE_KEY) return

      setSelectedAttributeKeys((current) => {
        const baseSelection = current.length ? current : allSelectableKeys

        if (baseSelection.includes(attributeKey)) {
          return baseSelection.filter((key) => key !== attributeKey)
        }

        return insertKeyInReferenceOrder(
          baseSelection,
          attributeKey,
          allSelectableKeys,
        )
      })
    }

    const toggleAttributeGroup = (attributes: ExportAttributeDefinition[]) => {
      const groupAttributeKeys = attributes
        .map((attribute) => attribute.key)
        .filter((key) => key !== PINNED_ATTRIBUTE_KEY)
      if (groupAttributeKeys.length === 0) return

      setSelectedAttributeKeys((current) => {
        const baseSelection = current.length ? current : allSelectableKeys
        const baseSelectionSet = new Set(baseSelection)
        const isGroupSelected = groupAttributeKeys.every((key) =>
          baseSelectionSet.has(key),
        )

        if (isGroupSelected) {
          return baseSelection.filter(
            (key) => !groupAttributeKeys.includes(key),
          )
        }

        return reorderKeysByReference(
          [...baseSelection, ...groupAttributeKeys],
          allSelectableKeys,
        )
      })
    }

    const removeAttribute = (attributeKey: string) => {
      if (attributeKey === PINNED_ATTRIBUTE_KEY) return

      setSelectedAttributeKeys((current) => {
        const baseSelection = current.length ? current : allSelectableKeys

        return baseSelection.filter((key) => key !== attributeKey)
      })
    }

    const moveAttribute = (attributeKey: string, direction: -1 | 1) => {
      if (attributeKey === PINNED_ATTRIBUTE_KEY) return

      const currentIndex = effectiveSelectedAttributeKeys.indexOf(attributeKey)
      if (currentIndex === -1) return

      const minimumIndex = 1
      const targetIndex = currentIndex + direction
      if (
        targetIndex < minimumIndex ||
        targetIndex >= effectiveSelectedAttributeKeys.length
      ) {
        return
      }

      const previousItemRects = captureSelectedItemRects()
      const next = [...effectiveSelectedAttributeKeys]
      const currentValue = next[currentIndex]
      const targetValue = next[targetIndex]

      if (!currentValue || !targetValue) return

      next[currentIndex] = targetValue
      next[targetIndex] = currentValue

      setSelectedAttributeKeys(next)
      animateSelectedItemsFrom(previousItemRects)
    }

    const handleSelectedColumnDragEnd = ({ active, over }: DragEndEvent) => {
      setInvalidDraggedAttributeKey(null)

      if (
        !over ||
        active.id === over.id ||
        active.id === PINNED_ATTRIBUTE_KEY
      ) {
        return
      }

      const activeKey = String(active.id)
      const overKey = String(over.id)

      if (overKey === PINNED_ATTRIBUTE_KEY) return

      setSelectedAttributeKeys((current) => {
        const baseSelection = current.length ? current : allSelectableKeys
        const activeIndex = baseSelection.indexOf(activeKey)
        const overIndex = baseSelection.indexOf(overKey)

        if (activeIndex === -1 || overIndex === -1) {
          return baseSelection
        }

        return arrayMove(baseSelection, activeIndex, Math.max(1, overIndex))
      })
    }

    const handleSelectedColumnDragOver = ({ active, over }: DragOverEvent) => {
      const activeKey = String(active.id)

      setInvalidDraggedAttributeKey(
        over?.id === PINNED_ATTRIBUTE_KEY && active.id !== PINNED_ATTRIBUTE_KEY
          ? activeKey
          : null,
      )
    }

    const handleUseRecommended = () => {
      setSelectedAttributeKeys(recommendedAttributeKeys)
    }

    const handleSelectAll = () => {
      setSelectedAttributeKeys(allSelectableKeys)
    }

    const handleResetOrder = () => {
      setSelectedAttributeKeys((current) =>
        reorderKeysByReference(
          current.length ? current : allSelectableKeys,
          allSelectableKeys,
        ),
      )
    }

    const handleContinueMode = () => {
      if (!modeSelection) return

      if (modeSelection !== 'configure') {
        void handleExport(quickExportAttributeKeys, quickExportHeaderNameMode)
        return
      }

      setSelectedAttributeKeys([])
      setExportHeaderNameMode(DEFAULT_EXPORT_HEADER_NAME_MODE)
      setStep(SELECT_COLUMNS_STEP)
    }

    const handleExport = async (
      attributeKeys = effectiveSelectedAttributeKeys,
      headerNameMode = exportHeaderNameMode,
    ) => {
      const normalizedAttributeKeys = normalizeStoredExportAttributeKeys(
        attributeKeys,
        allSelectableKeys,
      )

      if (
        isExporting ||
        isTooLarge ||
        totalCount === 0 ||
        normalizedAttributeKeys.length === 0
      ) {
        return
      }

      if (
        writeStoredExportConfig(schemaName, {
          attributeKeys: normalizedAttributeKeys,
          headerNameMode,
        })
      ) {
        setStoredAttributeKeys(normalizedAttributeKeys)
        setStoredHeaderNameMode(headerNameMode)
      }

      resetReviewState()
      setStep(EXPORT_STEP)
      setIsExporting(true)
      setIsExportDone(false)
      setHasExportError(false)
      setDownloadCsvAgain(undefined)
      setLastExportAttributeKeys(normalizedAttributeKeys)
      setLastExportHeaderNameMode(headerNameMode)
      setExportProgressStage('preparing')
      setExportProgress({ completed: 0, total: totalCount })

      try {
        const result = await exportListPersonsToCsv({
          listId,
          contactEntity,
          listTitle,
          attributeKeys: normalizedAttributeKeys,
          headerNameMode,
          onProgress: (progress) => {
            setExportProgressStage('processing')
            setExportProgress(progress)
          },
        })

        if (result.status === 'done') {
          setExportProgressStage('finalizing')
          setExportProgress({ completed: totalCount, total: totalCount })
          setDownloadCsvAgain(() => result.download)
          setIsExportDone(true)
        }
      } catch (error) {
        setHasExportError(true)
        errorToast(error as Error)
        reportError(error)
      } finally {
        setIsExporting(false)
      }
    }

    const handleRetryExport = () => {
      void handleExport(
        lastExportAttributeKeys.length
          ? lastExportAttributeKeys
          : effectiveSelectedAttributeKeys,
        lastExportAttributeKeys.length
          ? lastExportHeaderNameMode
          : exportHeaderNameMode,
      )
    }

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOutsideClick={false}
        className={classNames('export-csv-modal', {
          'has-preview-sidecar': step === REVIEW_STEP && isReviewPreviewOpen,
          'is-closing-preview-sidecar':
            step === REVIEW_STEP && isReviewPreviewClosing,
        })}
        style={
          {
            '--export-csv-modal-transition-duration':
              REVIEW_PREVIEW_TRANSITION_DURATION,
          } as CSSProperties
        }
      >
        <Modal.Header>
          <div className="export-csv-modal__header-copy">
            <h3 className="o-modal-title">
              {t(
                'components.csv.export.exportContactsTitle',
                'Export contacts',
              )}
            </h3>
            <div className="export-csv-modal__header-subtitle wizard-modal-text wizard-modal-text--compact">
              {t('components.csv.export.headerSelectedCount', {
                defaultValue: '{{count}} contacts selected for export',
                count: totalCount,
              })}
            </div>
          </div>
        </Modal.Header>

        {step === REVIEW_STEP && (
          <button
            type="button"
            className={classNames('export-csv-modal__preview-fab', {
              'is-active': isReviewPreviewOpen,
            })}
            aria-label={
              isReviewPreviewOpen
                ? t('components.csv.export.hidePreview', 'Hide preview')
                : t('components.csv.export.showPreview', 'Show preview')
            }
            aria-expanded={isReviewPreviewOpen}
            onClick={toggleReviewPreview}
          >
            <Icon
              icon={isReviewPreviewVisible ? 'cross' : 'search'}
              color="white"
              size="md"
            />
          </button>
        )}

        {isReviewPreviewVisible && (
          <aside
            className={classNames('export-csv-modal__preview-sidecar', {
              'is-closing': isReviewPreviewClosing,
            })}
            aria-label={t('components.csv.export.previewTitle', 'Preview')}
          >
            <div className="export-csv-modal__preview-sidecar-head">
              <div>
                <div className="export-csv-modal__panel-title wizard-modal-title">
                  {t('components.csv.export.previewTitle', 'Preview')}
                </div>
                <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                  {t(
                    'components.csv.export.previewText',
                    'A quick look at how the first rows will appear in the exported CSV.',
                  )}
                </div>
              </div>
              <button
                type="button"
                className="export-csv-modal__icon-button"
                aria-label={t(
                  'components.csv.export.hidePreview',
                  'Hide preview',
                )}
                onClick={closeReviewPreview}
              >
                <Icon icon="cross" size="sm" />
              </button>
            </div>

            <div className="export-csv-modal__preview-sidecar-meta">
              <span className="export-csv-modal__badge export-csv-modal__badge--info">
                {t('components.csv.export.previewBadge', {
                  defaultValue: 'First {{count}} contacts',
                  count: CSV_EXPORT_PREVIEW_ROWS,
                })}
              </span>
              <span className="export-csv-modal__badge">
                {t('components.csv.export.columnsBadge', {
                  defaultValue: '{{count}} columns',
                  count: effectiveSelectedAttributeKeys.length,
                })}
              </span>
            </div>

            <div className="export-csv-modal__table-wrap">
              {previewRows.length > 0 ? (
                <table className="export-csv-modal__table">
                  <thead>
                    <tr>
                      {effectiveSelectedAttributeKeys.map((key) => (
                        <th key={key}>
                          {getHeaderLabel(
                            key,
                            schemaAttributes,
                            exportHeaderNameMode,
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr key={row.id}>
                        {row.values.map((value, index) => (
                          <td
                            key={`${row.id}-${effectiveSelectedAttributeKeys[index]}`}
                          >
                            {value || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="export-csv-modal__table-empty">
                  {t(
                    'components.csv.export.previewEmpty',
                    'No contacts are available for preview.',
                  )}
                </div>
              )}
            </div>
          </aside>
        )}

        <Modal.Body>
          <WizardModalLayout className="export-csv-modal__layout">
            <StepTimeline
              activeStep={step}
              className="export-csv-modal__timeline"
              isStepClickable={(targetStep) =>
                !isExporting &&
                targetStep !== step &&
                visitedSteps.includes(targetStep)
              }
              onStepClick={handleTimelineStepClick}
              steps={stepTitles.slice(0, EXPORT_STEP_COUNT)}
            />

            <WizardModalStepContent className="export-csv-modal__step-content">
              {step === EXPORT_MODE_STEP && (
                <section className="export-csv-modal__mode-step">
                  <div className="export-csv-modal__step-head">
                    <div className="export-csv-modal__panel-title wizard-modal-title">
                      {t(
                        'components.csv.export.modeTitle',
                        'How would you like to export?',
                      )}
                    </div>
                    <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                      {t(
                        'components.csv.export.modeText',
                        'Download immediately or configure which columns to include first.',
                      )}
                    </div>
                  </div>

                  {isTooLarge && (
                    <div className="export-csv-modal__mode-error" role="alert">
                      <div className="export-csv-modal__mode-error-icon">
                        <Icon icon="alert-circle-solid" color="danger" />
                      </div>
                      <div>
                        <div className="export-csv-modal__mode-error-title wizard-modal-title wizard-modal-title--strong">
                          {t('components.csv.exportTooLarge.title', {
                            defaultValue:
                              'Export unavailable — selection exceeds {{limit}} contacts',
                            limit: MAX_SYNC_CSV_EXPORT_ROWS,
                          })}
                        </div>
                        <div className="export-csv-modal__mode-error-text wizard-modal-text">
                          {t('components.csv.exportTooLarge.text', {
                            defaultValue:
                              'CSV export is limited to {{limit}} contacts. Please narrow your filter and try again.',
                            limit: MAX_SYNC_CSV_EXPORT_ROWS,
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className="export-csv-modal__mode-options"
                    role="radiogroup"
                    aria-label={t(
                      'components.csv.export.modeTitle',
                      'How would you like to export?',
                    )}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={modeSelection === 'allColumns'}
                      className={classNames('export-csv-modal__mode-option', {
                        'is-selected': modeSelection === 'allColumns',
                      })}
                      onClick={() => setModeSelection('allColumns')}
                      disabled={!canStartExport}
                    >
                      <Radio
                        checked={modeSelection === 'allColumns'}
                        onChange={() => setModeSelection('allColumns')}
                        disabled={!canStartExport}
                        tabIndex={-1}
                        aria-hidden
                      />
                      <span className="export-csv-modal__mode-copy">
                        <span className="export-csv-modal__mode-title wizard-modal-title wizard-modal-title--strong">
                          {t(
                            'components.csv.export.modeAllColumnsTitle',
                            'Export all columns',
                          )}
                        </span>
                        <span className="export-csv-modal__mode-text wizard-modal-text">
                          {t('components.csv.export.modeAllColumnsText', {
                            defaultValue:
                              'Download all {{columns}} fields in the default order.',
                            columns: allSelectableKeys.length,
                          })}
                        </span>
                      </span>
                      <span className="export-csv-modal__badge">
                        {t('components.csv.export.columnsBadge', {
                          defaultValue: '{{count}} columns',
                          count: allSelectableKeys.length,
                        })}
                      </span>
                    </button>

                    {hasStoredExportConfiguration && (
                      <button
                        type="button"
                        role="radio"
                        aria-checked={modeSelection === 'lastUsed'}
                        className={classNames('export-csv-modal__mode-option', {
                          'is-selected': modeSelection === 'lastUsed',
                        })}
                        onClick={() => setModeSelection('lastUsed')}
                        disabled={!canStartExport}
                      >
                        <Radio
                          checked={modeSelection === 'lastUsed'}
                          onChange={() => setModeSelection('lastUsed')}
                          disabled={!canStartExport}
                          tabIndex={-1}
                          aria-hidden
                        />
                        <span className="export-csv-modal__mode-copy">
                          <span className="export-csv-modal__mode-title wizard-modal-title wizard-modal-title--strong">
                            {t(
                              'components.csv.export.modeLastUsedTitle',
                              'Use last saved settings',
                            )}
                          </span>
                          <span className="export-csv-modal__mode-text wizard-modal-text">
                            {t('components.csv.export.modeLastUsedText', {
                              defaultValue:
                                'Download the {{columns}} fields from your last export, in the same order.',
                              columns: storedExportAttributeKeys.length,
                            })}
                          </span>
                        </span>
                        <span className="export-csv-modal__badge">
                          {t('components.csv.export.columnsBadge', {
                            defaultValue: '{{count}} columns',
                            count: storedExportAttributeKeys.length,
                          })}
                        </span>
                      </button>
                    )}

                    <div className="export-csv-modal__mode-divider" aria-hidden>
                      <span>{t('common.or', 'or')}</span>
                    </div>

                    <button
                      type="button"
                      role="radio"
                      aria-checked={modeSelection === 'configure'}
                      className={classNames('export-csv-modal__mode-option', {
                        'is-selected': modeSelection === 'configure',
                      })}
                      onClick={() => setModeSelection('configure')}
                      disabled={isTooLarge}
                    >
                      <Radio
                        checked={modeSelection === 'configure'}
                        onChange={() => setModeSelection('configure')}
                        disabled={isTooLarge}
                        tabIndex={-1}
                        aria-hidden
                      />
                      <span className="export-csv-modal__mode-copy">
                        <span className="export-csv-modal__mode-title wizard-modal-title wizard-modal-title--strong">
                          {t(
                            'components.csv.export.modeConfigureTitle',
                            'Configure columns first',
                          )}
                        </span>
                        <span className="export-csv-modal__mode-text wizard-modal-text">
                          {t(
                            'components.csv.export.modeConfigureText',
                            'Choose which columns to include and set their order before exporting.',
                          )}
                        </span>
                      </span>
                      <span className="export-csv-modal__badge">
                        {t('components.csv.export.availableBadge', {
                          defaultValue: '{{count}} available',
                          count: allSelectableKeys.length,
                        })}
                      </span>
                    </button>
                  </div>
                </section>
              )}

              {step === SELECT_COLUMNS_STEP && (
                <div className="export-csv-modal__content">
                  <section className="export-csv-modal__selection-summary">
                    <div>
                      <div className="export-csv-modal__panel-title wizard-modal-title">
                        {t('components.csv.export.selectedSummary', {
                          defaultValue: '{{columns}} columns selected',
                          columns: effectiveSelectedAttributeKeys.length,
                        })}
                      </div>
                      <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                        {t(
                          'components.csv.export.selectColumnsText',
                          'Choose the attributes that should be included in the CSV.',
                        )}
                      </div>
                    </div>
                    <span className="export-csv-modal__badge export-csv-modal__badge--info">
                      {t('components.csv.export.contactsBadge', {
                        defaultValue: '{{count}} contacts',
                        count: totalCount,
                      })}
                    </span>
                  </section>

                  <div className="export-csv-modal__toolbar">
                    <Search
                      name="export-attributes-search"
                      value={searchValue}
                      onChange={onSearchChange}
                      bgColor="white"
                      placeholder={t(
                        'components.csv.export.searchPlaceholder',
                        'Search attributes',
                      )}
                      searchWrapperClassName="export-csv-modal__search"
                    />
                    <div className="export-csv-modal__preset-actions">
                      <Button
                        variant="outlined"
                        buttonClassName={classNames(
                          'export-csv-modal__preset-button',
                          { 'is-active': isRecommendedSelection },
                        )}
                        onClick={handleUseRecommended}
                      >
                        {t('components.csv.export.recommended', 'Recommended')}
                      </Button>
                      <Button
                        variant="outlined"
                        buttonClassName={classNames(
                          'export-csv-modal__preset-button',
                          { 'is-active': isAllFieldsSelection },
                        )}
                        onClick={handleSelectAll}
                      >
                        {t('components.csv.export.allFields', 'All fields')}
                      </Button>
                    </div>
                  </div>

                  <section className="export-csv-modal__panel export-csv-modal__panel--selection">
                    <div className="export-csv-modal__panel-head">
                      <div className="export-csv-modal__panel-title wizard-modal-title">
                        {t(
                          'components.csv.export.availableAttributes',
                          'Available attributes',
                        )}
                      </div>
                    </div>

                    <div className="export-csv-modal__groups">
                      {hasSearchResults ? (
                        filteredAttributeGroups.map((group) => {
                          const selectedCount = group.attributes.filter(
                            (attribute) =>
                              selectedAttributeSet.has(attribute.key),
                          ).length
                          const groupSelectionState =
                            selectedCount === group.attributes.length
                              ? true
                              : selectedCount === 0
                                ? false
                                : undefined

                          return (
                            <section
                              className="export-csv-modal__group"
                              key={group.key}
                            >
                              <div className="export-csv-modal__group-head">
                                <label className="export-csv-modal__group-toggle">
                                  <ControlledCheckbox
                                    checked={groupSelectionState}
                                    onChange={() =>
                                      toggleAttributeGroup(group.attributes)
                                    }
                                  />
                                  <span className="export-csv-modal__group-title wizard-modal-title">
                                    {group.title}
                                  </span>
                                </label>
                                <div className="export-csv-modal__group-meta wizard-modal-text">
                                  {t('components.csv.export.groupSelection', {
                                    defaultValue:
                                      '{{selected}} of {{total}} selected',
                                    selected: selectedCount,
                                    total: group.attributes.length,
                                  })}
                                </div>
                              </div>

                              <div className="export-csv-modal__group-items">
                                {group.attributes.map((attribute) => {
                                  const checked = selectedAttributeSet.has(
                                    attribute.key,
                                  )

                                  return (
                                    <button
                                      type="button"
                                      className={classNames(
                                        'export-csv-modal__attribute-item',
                                        { 'is-selected': checked },
                                      )}
                                      key={attribute.key}
                                      onClick={() =>
                                        toggleAttribute(attribute.key)
                                      }
                                    >
                                      <div className="export-csv-modal__attribute-main">
                                        <ControlledCheckbox
                                          checked={checked}
                                          onChange={() => undefined}
                                        />
                                        <div className="export-csv-modal__attribute-copy">
                                          <div className="export-csv-modal__attribute-label wizard-modal-label">
                                            {attribute.label}
                                          </div>
                                          <div className="export-csv-modal__attribute-key wizard-modal-meta">
                                            {attribute.key}
                                          </div>
                                        </div>
                                      </div>
                                      {checked && (
                                        <span className="export-csv-modal__attribute-action">
                                          <Icon icon="cross" size="md" />
                                        </span>
                                      )}
                                    </button>
                                  )
                                })}
                              </div>
                            </section>
                          )
                        })
                      ) : (
                        <div className="export-csv-modal__empty-state">
                          <div className="export-csv-modal__empty-state-title wizard-modal-label">
                            {t(
                              'components.csv.export.emptySearchTitle',
                              'No matching attributes',
                            )}
                          </div>
                          <div className="export-csv-modal__empty-state-text wizard-modal-text">
                            {t(
                              'components.csv.export.emptySearchText',
                              'Try a different search term or reset the filter.',
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {step === REVIEW_STEP && (
                <div className="export-csv-modal__review">
                  <section className="export-csv-modal__selection-summary">
                    <div>
                      <div className="export-csv-modal__panel-title wizard-modal-title">
                        {t(
                          'components.csv.export.reviewTitle',
                          'Order & review',
                        )}
                      </div>
                      <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                        {t(
                          'components.csv.export.reviewText',
                          'Check the selected columns, adjust their order and verify the preview.',
                        )}
                      </div>
                    </div>
                    <span className="export-csv-modal__badge export-csv-modal__badge--info">
                      {t('components.csv.export.reviewSummary', {
                        defaultValue:
                          '{{contacts}} contacts · {{columns}} columns',
                        contacts: totalCount,
                        columns: effectiveSelectedAttributeKeys.length,
                      })}
                    </span>
                  </section>

                  <section className="export-csv-modal__header-settings">
                    <div>
                      <div className="export-csv-modal__panel-title wizard-modal-title">
                        {t(
                          'components.csv.export.headerNamesTitle',
                          'CSV headers',
                        )}
                      </div>
                      <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                        {t(
                          'components.csv.export.headerNamesText',
                          'Choose how column headers should be named in the exported file.',
                        )}
                      </div>
                    </div>
                    <label className="export-csv-modal__header-name-option">
                      <ControlledCheckbox
                        checked={exportHeaderNameMode === 'schema'}
                        onChange={(event) =>
                          setExportHeaderNameMode(
                            event.target.checked
                              ? 'schema'
                              : DEFAULT_EXPORT_HEADER_NAME_MODE,
                          )
                        }
                      />
                      <span className="export-csv-modal__header-name-copy">
                        <span className="export-csv-modal__header-name-title wizard-modal-title--sm">
                          {t(
                            'components.csv.export.useSchemaHeaderNames',
                            'Use schema names as CSV headers',
                          )}
                        </span>
                        <span className="export-csv-modal__header-name-text wizard-modal-text wizard-modal-text--sm">
                          {t(
                            'components.csv.export.useSchemaHeaderNamesText',
                            'Exports attribute keys such as givenName instead of translated labels.',
                          )}
                        </span>
                      </span>
                    </label>
                  </section>

                  <section className="export-csv-modal__panel">
                    <div className="export-csv-modal__panel-head export-csv-modal__panel-head--split">
                      <div>
                        <div className="export-csv-modal__panel-title wizard-modal-title">
                          {t(
                            'components.csv.export.selectedColumns',
                            'Selected columns',
                          )}
                        </div>
                        <div className="export-csv-modal__panel-subtitle wizard-modal-text wizard-modal-text--compact">
                          {t(
                            'components.csv.export.selectedColumnsHint',
                            'Reorder the columns exactly as they should appear in the file.',
                          )}
                        </div>
                      </div>
                      <div className="export-csv-modal__panel-actions">
                        <Button
                          variant="outlined"
                          buttonClassName="export-csv-modal__preset-button"
                          onClick={handleResetOrder}
                        >
                          {t('components.csv.export.resetOrder', 'Reset order')}
                        </Button>
                      </div>
                    </div>

                    <DndContext
                      sensors={selectedColumnSensors}
                      collisionDetection={closestCenter}
                      onDragCancel={() => setInvalidDraggedAttributeKey(null)}
                      onDragEnd={handleSelectedColumnDragEnd}
                      onDragOver={handleSelectedColumnDragOver}
                    >
                      <SortableContext
                        items={selectedAttributes.map(
                          (attribute) => attribute.key,
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <div
                          className="export-csv-modal__selected-list"
                          ref={selectedListRef}
                        >
                          {selectedAttributes.map((attribute, index) => {
                            const canMoveUp =
                              attribute.key !== PINNED_ATTRIBUTE_KEY &&
                              index > 1
                            const canMoveDown =
                              attribute.key !== PINNED_ATTRIBUTE_KEY &&
                              index < selectedAttributes.length - 1
                            const isPinned =
                              attribute.key === PINNED_ATTRIBUTE_KEY

                            return (
                              <SortableSelectedAttributeItem
                                attribute={attribute}
                                canMoveDown={canMoveDown}
                                canMoveUp={canMoveUp}
                                dragLabel={`Reorder ${attribute.label}`}
                                fixedColumnsLabel={t(
                                  'components.csv.export.fixedColumns',
                                  'Fixed columns',
                                )}
                                index={index}
                                isInvalidDropTarget={
                                  invalidDraggedAttributeKey === attribute.key
                                }
                                isPinned={isPinned}
                                key={attribute.key}
                                onMove={moveAttribute}
                                onRemove={removeAttribute}
                                pinnedLabel={t(
                                  'components.csv.export.alwaysIncluded',
                                  'Always included',
                                )}
                              />
                            )
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </section>
                </div>
              )}

              {step === EXPORT_STEP && (
                <WizardModalProgressState className="export-csv-modal__progress-content">
                  <div
                    className={classNames('export-csv-modal__progress-icon', {
                      'is-complete': isExportDone,
                      'is-error': hasExportError,
                    })}
                  >
                    <Icon
                      icon={
                        hasExportError
                          ? 'alert-circle-solid'
                          : isExportDone
                            ? 'check'
                            : 'download-circle'
                      }
                      color={
                        hasExportError
                          ? 'danger'
                          : isExportDone
                            ? 'success'
                            : 'strong'
                      }
                    />
                  </div>
                  <div className="export-csv-modal__progress-head">
                    <div className="export-csv-modal__progress-title wizard-modal-title">
                      {hasExportError
                        ? t('components.csv.export.errorTitle', 'Export failed')
                        : isExportDone
                          ? t(
                              'components.csv.export.completeTitle',
                              'Export complete',
                            )
                          : t(
                              'components.csv.export.progressTitle',
                              'Exporting CSV',
                            )}
                    </div>
                    <div className="export-csv-modal__progress-text wizard-modal-text">
                      {hasExportError
                        ? t(
                            'components.csv.export.errorText',
                            'The CSV file could not be created. Please try again.',
                          )
                        : isExportDone
                          ? t(
                              'components.csv.export.completeText',
                              'Your CSV file has been downloaded.',
                            )
                          : t(
                              'components.csv.export.progressText',
                              'Please keep this window open until the export is complete.',
                            )}
                    </div>
                  </div>
                  {!isExportDone && !hasExportError && (
                    <div className="export-csv-modal__progress-stage wizard-modal-text">
                      {isPreparingExport
                        ? t(
                            'components.csv.export.progressPreparing',
                            'Preparing export data...',
                          )
                        : exportProgressStage === 'finalizing'
                          ? t(
                              'components.csv.export.progressFinalizing',
                              'Finalizing file...',
                            )
                          : t(
                              'components.csv.export.progressProcessing',
                              'Generating file...',
                            )}
                    </div>
                  )}
                  {!hasExportError && (
                    <WizardModalProgressBar
                      className="export-csv-modal__export-progress"
                      isIndeterminate={isPreparingExport && !isExportDone}
                      value={displayedExportProgressPercent}
                    />
                  )}
                  {!isPreparingExport && !hasExportError && (
                    <div className="wizard-modal-progress-count">
                      {exportProgress.completed} / {exportProgress.total}
                    </div>
                  )}
                  {!isExportDone && !hasExportError && (
                    <div className="wizard-modal-progress-note">
                      {t(
                        'components.csv.export.progressNotice',
                        'This may take a few moments.',
                      )}
                    </div>
                  )}
                </WizardModalProgressState>
              )}
            </WizardModalStepContent>
          </WizardModalLayout>
        </Modal.Body>

        <Modal.Footer>
          <WizardModalFooter className="export-csv-modal__footer">
            {isExportStep ? (
              <>
                {isExporting && (
                  <div className="export-csv-modal__footer-status">
                    {t('components.csv.export.progressTitle', 'Exporting CSV')}
                  </div>
                )}
                {hasExportError && !isExporting && (
                  <>
                    <Button variant="outlined" onClick={handleBack}>
                      {t('common.back', 'Back')}
                    </Button>
                    <WizardModalFooterNav className="export-csv-modal__footer-nav">
                      <Button variant="outlined" onClick={handleClose}>
                        {t('common.close', 'Close')}
                      </Button>
                      <Button variant="primary" onClick={handleRetryExport}>
                        {t('components.csv.export.retry', 'Retry')}
                      </Button>
                    </WizardModalFooterNav>
                  </>
                )}
                {isExportDone && !hasExportError && (
                  <WizardModalFooterNav className="export-csv-modal__footer-nav">
                    <Button variant="outlined" onClick={handleClose}>
                      {t('common.close', 'Close')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={downloadCsvAgain}
                      icon="download-circle"
                      disabled={!downloadCsvAgain}
                    >
                      {t(
                        'components.csv.export.downloadAgain',
                        'Download again',
                      )}
                    </Button>
                  </WizardModalFooterNav>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isExporting}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <WizardModalFooterNav className="export-csv-modal__footer-nav">
                  {step > EXPORT_MODE_STEP && (
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      disabled={isExporting}
                    >
                      {t('common.back', 'Back')}
                    </Button>
                  )}
                  {step === EXPORT_MODE_STEP && (
                    <Button
                      variant="primary"
                      onClick={handleContinueMode}
                      disabled={!canContinueMode}
                      icon={
                        modeSelection === 'configure'
                          ? undefined
                          : 'download-circle'
                      }
                    >
                      {modeSelection === 'configure'
                        ? t('common.continue', 'Continue')
                        : t('components.csv.export.exportAction', 'Export')}
                    </Button>
                  )}
                  {step === SELECT_COLUMNS_STEP && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        resetReviewState()
                        setStep(REVIEW_STEP)
                      }}
                      disabled={!canContinueSelection}
                    >
                      {t('common.next', 'Next')}
                    </Button>
                  )}
                  {step === REVIEW_STEP && (
                    <Button
                      variant="primary"
                      onClick={() => void handleExport()}
                      disabled={!canStartConfiguredExport}
                      icon="download-circle"
                    >
                      {t('components.csv.export.startExport', 'Start export')}
                    </Button>
                  )}
                </WizardModalFooterNav>
              </>
            )}
          </WizardModalFooter>
        </Modal.Footer>
      </Modal>
    )
  },
)
